import { useMemo, useCallback } from "react";
import { calcularComisionEdificiosTotal, calcularImporteNetoIntermediario, calculateComission } from "../../../utils";

/**
 * Hook que calcula ingresos mensuales y comisión de edificios de contratos (sales_contratos).
 *
 * Explota el JSONB `meses` de cada contrato y agrupa por mes según `facturafecha`.
 * ER solo cuenta meses realmente facturados (con facturafecha), no proyectados.
 */
export function useContratosCalcs({
  contratosChile,
  contratosPeru,
  selectedYear,
  modoCombinado,
  dolarFallback,
  dolarMensualChile,
  dolarMensualPeru,
  pais,
  edificios,
  edificiosChile,
  edificiosPeru,
  clientes,
  clientesChile,
  clientesPeru,
  intermediarios,
  intermediariosChile,
  intermediariosPeru,
}) {
  // Helper: calcula ingresos mensuales explotando JSONB meses
  const calcIngresosContratos = useCallback((data, filterYear, dolarRate) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha) return; // ER: solo facturado real
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year === filterYear && monthIndex >= 0 && monthIndex < 12) {
            const importe = Number(mesObj.importe) || 0;
            valores[monthIndex] += c.moneda_dolar ? importe * dolarRate : importe;
          }
        });
      });
    return valores;
  }, []);

  // Helper: backward-compat para campos por tipo de pantalla
  const resolverCamposTipo = (mesObj, edificiosMap) => {
    let pAsc = mesObj.pantallas_ascensores || "";
    let pEsp = mesObj.pantallas_espera || "";
    let pCir = mesObj.pantallas_circulacion || "";
    let pCow = mesObj.pantallas_cowork || "";
    if (!pAsc && !pEsp && !pCir && !pCow && mesObj.edificios_ids) {
      const ids = mesObj.edificios_ids
        .split(",")
        .map((s) => Number(s.trim()))
        .filter(Boolean);
      const asc = [];
      const esp = [];
      const cir = [];
      const cow = [];
      ids.forEach((eid) => {
        const e = edificiosMap[eid];
        if (!e) return;
        if ((Number(e.pantallas) || 0) > 0) asc.push(eid);
        if ((Number(e.espera) || 0) > 0) esp.push(eid);
        if ((Number(e.totems) || 0) > 0) cir.push(eid);
        if ((Number(e.cowork) || 0) > 0) cow.push(eid);
      });
      pAsc = asc.join(",");
      pEsp = esp.join(",");
      pCir = cir.join(",");
      pCow = cow.join(",");
    }
    return { pAsc, pEsp, pCir, pCow };
  };

  // Helper: calcula comisión edificios mensual explotando JSONB meses
  const calcComisionEdificiosContratos = useCallback(
    (data, filterYear, dolarRate, edificiosMap, clientesArr, intermediariosArr, paisCalc) => {
      const valores = Array(12).fill(0);
      if (!edificiosMap || Object.keys(edificiosMap).length === 0) return valores;

      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            if (!mesObj.edificios_ids) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = Number(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * dolarRate : importe;
            const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificiosMap);

            const oppObj = {
              edificiosparticipantes: mesObj.edificios_ids,
              pantallastotales: mesObj.pantallas_totales || 0,
              pantallas_ascensores: pAsc,
              pantallas_espera: pEsp,
              pantallas_circulacion: pCir,
              pantallas_cowork: pCow,
              facturafecha: mesObj.facturafecha,
              importe: importeLocal,
              client_id: c.client_id,
              isBTL: false,
            };

            const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, paisCalc);
            valores[monthIndex] += calcularComisionEdificiosTotal(oppObj, edificiosMap, neto);
          });
        });
      return valores;
    },
    []
  );

  // Helper: calcula comisión agencia mensual explotando JSONB meses
  const calcComisionAgenciaContratos = useCallback((data, filterYear, clientesArr, intermediariosArr, paisCalc) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha) return;
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

          const importeFull = parseFloat(mesObj.importe) || 0;
          const oppObj = {
            importe: importeFull,
            client_id: c.client_id,
            facturafecha: mesObj.facturafecha,
            isBTL: false,
          };
          const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, paisCalc);
          valores[monthIndex] += importeFull - neto;
        });
      });
    return valores;
  }, []);

  // Helper: calcula comisión comercial mensual explotando JSONB meses
  const calcComisionComercialContratos = useCallback(
    (data, filterYear, clientesMap, intermediariosMap, edificiosMap, paisCalc) => {
      const valores = Array(12).fill(0);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificiosMap || {});
            const oppObj = {
              importe: mesObj.importe,
              client_id: c.client_id,
              pais: paisCalc,
              facturafecha: mesObj.facturafecha,
              ejecutivo: c.ejecutivo,
              mes_cierre: c.mes_cierre,
              moneda_dolar: c.moneda_dolar,
              isBTL: false,
              edificiosparticipantes: mesObj.edificios_ids || "",
              pantallastotales: mesObj.pantallas_totales || 0,
              pantallas_ascensores: pAsc,
              pantallas_espera: pEsp,
              pantallas_circulacion: pCir,
              pantallas_cowork: pCow,
            };
            valores[monthIndex] += calculateComission.calculateSalesCommission(
              oppObj,
              clientesMap,
              intermediariosMap,
              edificiosMap
            );
          });
        });
      return valores;
    },
    []
  );

  // País actual
  const contratosData = pais === "Chile" ? contratosChile : contratosPeru;
  const rate = dolarFallback[pais] || 1;

  // Memoize arrays para evitar re-renders en dependencias de useMemo
  const clientesArr = useMemo(() => (clientes ? Object.values(clientes) : []), [clientes]);
  const intermediariosArr = useMemo(() => (intermediarios ? Object.values(intermediarios) : []), [intermediarios]);
  const clientesChileArr = useMemo(() => (clientesChile ? Object.values(clientesChile) : []), [clientesChile]);
  const intermediariosChileArr = useMemo(
    () => (intermediariosChile ? Object.values(intermediariosChile) : []),
    [intermediariosChile]
  );
  const clientesPeruArr = useMemo(() => (clientesPeru ? Object.values(clientesPeru) : []), [clientesPeru]);
  const intermediariosPeruArr = useMemo(
    () => (intermediariosPeru ? Object.values(intermediariosPeru) : []),
    [intermediariosPeru]
  );

  // ─── INGRESOS ───

  const ingresosContratosMensual = useMemo(
    () => calcIngresosContratos(contratosData, selectedYear, rate),
    [calcIngresosContratos, contratosData, selectedYear, rate]
  );

  const ingresosContratosMensualAnoAnterior = useMemo(
    () => calcIngresosContratos(contratosData, selectedYear - 1, rate),
    [calcIngresosContratos, contratosData, selectedYear, rate]
  );

  const ingresosContratosMensualChile = useMemo(
    () => calcIngresosContratos(contratosChile, selectedYear, dolarFallback.Chile),
    [calcIngresosContratos, contratosChile, selectedYear, dolarFallback]
  );

  const ingresosContratosMensualPeru = useMemo(
    () => calcIngresosContratos(contratosPeru, selectedYear, dolarFallback["Perú"]),
    [calcIngresosContratos, contratosPeru, selectedYear, dolarFallback]
  );

  // Combinado en USD (para modo región)
  const contratosMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;
    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      const valorChile = ingresosContratosMensualChile[i] || 0;
      const dolarChile = dolarMensualChile[i] || dolarFallback.Chile;
      const chileUSD = valorChile / dolarChile;

      const valorPeru = ingresosContratosMensualPeru[i] || 0;
      const dolarPeru = dolarMensualPeru[i] || dolarFallback["Perú"];
      const peruUSD = valorPeru / dolarPeru;

      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [
    modoCombinado,
    ingresosContratosMensualChile,
    ingresosContratosMensualPeru,
    dolarMensualChile,
    dolarMensualPeru,
    dolarFallback,
  ]);

  // ─── COMISIÓN EDIFICIOS de contratos ───

  const comisionEdificiosContratosMensual = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosData,
        selectedYear,
        rate,
        edificios,
        clientesArr,
        intermediariosArr,
        pais
      ),
    [calcComisionEdificiosContratos, contratosData, selectedYear, rate, edificios, clientesArr, intermediariosArr, pais]
  );

  const comisionEdificiosContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosData,
        selectedYear - 1,
        rate,
        edificios,
        clientesArr,
        intermediariosArr,
        pais
      ),
    [calcComisionEdificiosContratos, contratosData, selectedYear, rate, edificios, clientesArr, intermediariosArr, pais]
  );

  const comisionEdificiosContratosMensualChile = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosChile,
        selectedYear,
        dolarFallback.Chile,
        edificiosChile,
        clientesChileArr,
        intermediariosChileArr,
        "Chile"
      ),
    [
      calcComisionEdificiosContratos,
      contratosChile,
      selectedYear,
      dolarFallback,
      edificiosChile,
      clientesChileArr,
      intermediariosChileArr,
    ]
  );

  const comisionEdificiosContratosMensualPeru = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosPeru,
        selectedYear,
        dolarFallback["Perú"],
        edificiosPeru,
        clientesPeruArr,
        intermediariosPeruArr,
        "Perú"
      ),
    [
      calcComisionEdificiosContratos,
      contratosPeru,
      selectedYear,
      dolarFallback,
      edificiosPeru,
      clientesPeruArr,
      intermediariosPeruArr,
    ]
  );

  // ─── COMISIÓN AGENCIA de contratos ───

  const comisionAgenciaContratosMensual = useMemo(
    () => calcComisionAgenciaContratos(contratosData, selectedYear, clientesArr, intermediariosArr, pais),
    [calcComisionAgenciaContratos, contratosData, selectedYear, clientesArr, intermediariosArr, pais]
  );

  const comisionAgenciaContratosMensualAnoAnterior = useMemo(
    () => calcComisionAgenciaContratos(contratosData, selectedYear - 1, clientesArr, intermediariosArr, pais),
    [calcComisionAgenciaContratos, contratosData, selectedYear, clientesArr, intermediariosArr, pais]
  );

  const comisionAgenciaContratosMensualChile = useMemo(
    () => calcComisionAgenciaContratos(contratosChile, selectedYear, clientesChileArr, intermediariosChileArr, "Chile"),
    [calcComisionAgenciaContratos, contratosChile, selectedYear, clientesChileArr, intermediariosChileArr]
  );

  const comisionAgenciaContratosMensualPeru = useMemo(
    () => calcComisionAgenciaContratos(contratosPeru, selectedYear, clientesPeruArr, intermediariosPeruArr, "Perú"),
    [calcComisionAgenciaContratos, contratosPeru, selectedYear, clientesPeruArr, intermediariosPeruArr]
  );

  // ─── COMISIÓN COMERCIAL de contratos ───

  const comisionComercialContratosMensual = useMemo(
    () => calcComisionComercialContratos(contratosData, selectedYear, clientes, intermediarios, edificios, pais),
    [calcComisionComercialContratos, contratosData, selectedYear, clientes, intermediarios, edificios, pais]
  );

  const comisionComercialContratosMensualAnoAnterior = useMemo(
    () => calcComisionComercialContratos(contratosData, selectedYear - 1, clientes, intermediarios, edificios, pais),
    [calcComisionComercialContratos, contratosData, selectedYear, clientes, intermediarios, edificios, pais]
  );

  const comisionComercialContratosMensualChile = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosChile,
        selectedYear,
        clientesChile,
        intermediariosChile,
        edificiosChile,
        "Chile"
      ),
    [calcComisionComercialContratos, contratosChile, selectedYear, clientesChile, intermediariosChile, edificiosChile]
  );

  const comisionComercialContratosMensualPeru = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosPeru,
        selectedYear,
        clientesPeru,
        intermediariosPeru,
        edificiosPeru,
        "Perú"
      ),
    [calcComisionComercialContratos, contratosPeru, selectedYear, clientesPeru, intermediariosPeru, edificiosPeru]
  );

  return {
    ingresosContratosMensual,
    ingresosContratosMensualAnoAnterior,
    ingresosContratosMensualChile,
    ingresosContratosMensualPeru,
    contratosMensualesCombinados,
    comisionEdificiosContratosMensual,
    comisionEdificiosContratosMensualAnoAnterior,
    comisionEdificiosContratosMensualChile,
    comisionEdificiosContratosMensualPeru,
    comisionAgenciaContratosMensual,
    comisionAgenciaContratosMensualAnoAnterior,
    comisionAgenciaContratosMensualChile,
    comisionAgenciaContratosMensualPeru,
    comisionComercialContratosMensual,
    comisionComercialContratosMensualAnoAnterior,
    comisionComercialContratosMensualChile,
    comisionComercialContratosMensualPeru,
  };
}
